import React, { useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { motion } from "framer-motion";
import { db } from "../firebase/firebase-config";

const initialForm = {
  bookerName: "",
  bookingSite: "",
  itemDescription: "",
  cardHolder: "",
  cardNumber: "",
  transactionAmount: "",
  remarks: "",
  website: "",
};

const MobileBookingEntry = () => {
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "transactionAmount") {
      nextValue = value.replace(/[^\d.]/g, "");
    }

    if (name === "cardNumber") {
      nextValue = value.replace(/[^\dXx* -]/g, "").slice(0, 25);
    }

    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!form.bookerName.trim() || !form.bookingSite.trim() || !form.itemDescription.trim() || !form.cardHolder.trim()) {
      setErrorMessage("Please fill all required booking details.");
      return;
    }

    if (!form.cardNumber.trim()) {
      setErrorMessage("Card number is required.");
      return;
    }

    if (!form.transactionAmount || Number(form.transactionAmount) <= 0) {
      setErrorMessage("Enter a valid transaction amount.");
      return;
    }

    if (form.website.trim()) {
      setErrorMessage("Unable to submit right now. Please refresh and try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, "mobile-bookings"), {
        bookerName: form.bookerName.trim(),
        bookingSite: form.bookingSite.trim(),
        itemDescription: form.itemDescription.trim(),
        cardHolder: form.cardHolder.trim(),
        cardNumber: form.cardNumber.trim(),
        remarks: form.remarks.trim(),
        transactionAmount: Number(form.transactionAmount || 0),
        status: "submitted",
        createdAt: new Date().toISOString(),
        statusUpdatedAt: new Date().toISOString(),
      });

      setForm(initialForm);
      setSuccessMessage("Mobile booking submitted successfully.");
    } catch (error) {
      console.error("Unable to submit mobile booking:", error);
      setErrorMessage("Unable to submit booking right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="container-fluid py-4 px-2 px-md-3"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(14,165,233,0.12), transparent 24%), radial-gradient(circle at bottom right, rgba(249,115,22,0.12), transparent 28%), #f8fbff",
      }}
    >
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="row justify-content-center">
          <div className="col-12 col-xl-9">
            <div
              className="rounded-5 p-4 p-md-5"
              style={{
                background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(148,163,184,0.18)",
                boxShadow: "0 22px 44px rgba(15,23,42,0.08)",
              }}
            >
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-12 d-none">
                    <label className="form-label fw-bold">Website</label>
                    <input
                      type="text"
                      className="form-control"
                      name="website"
                      value={form.website}
                      onChange={handleChange}
                      autoComplete="off"
                      tabIndex="-1"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Booker Name</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="bookerName"
                      value={form.bookerName}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Booking Site</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="bookingSite"
                      value={form.bookingSite}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Item Description</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="itemDescription"
                      value={form.itemDescription}
                      onChange={handleChange}
                      placeholder="Mobile model, color, variant, notes"
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Card Holder</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="cardHolder"
                      value={form.cardHolder}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Card Number</label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      name="cardNumber"
                      value={form.cardNumber}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Transaction Amount</label>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      name="transactionAmount"
                      value={form.transactionAmount}
                      onChange={handleChange}
                      required
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Remarks</label>
                    <textarea
                      className="form-control"
                      name="remarks"
                      value={form.remarks}
                      onChange={handleChange}
                      rows="3"
                      style={{ borderRadius: 16 }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-success btn-lg mt-4"
                  disabled={isSubmitting}
                  style={{ borderRadius: 16, minWidth: 220 }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Mobile Booking"}
                </button>
              </form>

              {successMessage && <div className="alert alert-success rounded-4 mt-4 mb-0">{successMessage}</div>}
              {errorMessage && <div className="alert alert-danger rounded-4 mt-4 mb-0">{errorMessage}</div>}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MobileBookingEntry;
